import type { MediaResolver, Variant } from '../editor/lod';
import { resolveVariant, STILL_VARIANTS, type StoredVariant } from './variants';

/** `mime`: the original file's type; `expected`: the variants this asset has (or will have once uploaded). */
export type AssetMeta = { assetId: string; sha256: string; w: number; h: number; thumbhash: string; mime: string; expected: StoredVariant[] };
export type VariantBlobs = Partial<Record<StoredVariant, Blob>>;
/** An upload still to finish for a board (server boards, spec §10.1 step 7): survives a reload. */
export type UploadRecord = { assetId: string; boardId: string; name: string; done: StoredVariant[] };

export interface MediaBackend {
  /** Metadata and the variants are written together: never a half-stored asset. */
  putAsset(meta: AssetMeta, variants: VariantBlobs): Promise<void>;
  loadAll(): Promise<{ meta: AssetMeta; variants: VariantBlobs }[]>;
  putUpload(r: UploadRecord): Promise<void>;
  listUploads(boardId: string): Promise<UploadRecord[]>;
  deleteUpload(assetId: string): Promise<void>;
  close(): void;
}

/** Version 1 rows (P1) had neither `mime` nor `expected`. */
const withDefaults = (m: Partial<AssetMeta> & Pick<AssetMeta, 'assetId'>): AssetMeta => ({
  sha256: '', w: 1, h: 1, thumbhash: '', mime: 'image/webp', expected: STILL_VARIANTS, ...m,
});

export function memoryBackend(): MediaBackend {
  const assets = new Map<string, { meta: AssetMeta; variants: VariantBlobs }>();
  const uploads = new Map<string, UploadRecord>();
  return {
    async putAsset(meta, variants) {
      assets.set(meta.assetId, { meta, variants: { ...variants } });
    },
    async loadAll() {
      return [...assets.values()];
    },
    async putUpload(r) {
      uploads.set(r.assetId, structuredClone(r));
    },
    async listUploads(boardId) {
      return [...uploads.values()].filter((r) => r.boardId === boardId).map((r) => structuredClone(r));
    },
    async deleteUpload(assetId) {
      uploads.delete(assetId);
    },
    close() {},
  };
}

const done = <T>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
const committed = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  });

/** A variant's bytes, stored when IndexedDB refuses the Blob itself (Safari private windows, P12a task 10). */
type StoredBytes = { type: string; bytes: ArrayBuffer };

/**
 * Safari private windows (WebKit ephemeral sessions) fail a transaction that holds a Blob, then leave it stuck: every
 * later write to the same stores waits forever, even after `abort()`. The question is asked once, in a database of
 * its own, so a stuck probe never blocks the media database.
 */
async function canStoreBlobs(name: string): Promise<boolean> {
  const open = indexedDB.open(name, 1);
  open.onupgradeneeded = () => open.result.createObjectStore('probe');
  const db = await done(open).catch(() => null);
  if (!db) return false;
  const tx = db.transaction('probe', 'readwrite');
  try {
    tx.objectStore('probe').put(new Blob(['probe']), 'probe');
  } catch {
    return false;
  }
  return new Promise((resolve) => {
    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };
    tx.onerror = () => resolve(false);
    tx.onabort = () => resolve(false);
  });
}

/**
 * Local asset storage: `assets` (metadata by assetId), `blobs` (key `${assetId}/${variant}`) and, since v2, `uploads`.
 * Blobs are stored as Blobs (the browser keeps them on disk); where IndexedDB refuses them, as their bytes.
 */
export async function indexedDbBackend(name = 'os-media'): Promise<MediaBackend> {
  const open = indexedDB.open(name, 2);
  open.onupgradeneeded = () => {
    const db = open.result;
    if (!db.objectStoreNames.contains('assets')) db.createObjectStore('assets', { keyPath: 'assetId' });
    if (!db.objectStoreNames.contains('blobs')) db.createObjectStore('blobs');
    if (!db.objectStoreNames.contains('uploads')) db.createObjectStore('uploads', { keyPath: 'assetId' });
  };
  const db = await done(open);
  let blobsOk: Promise<boolean> | undefined;
  const write = async (meta: AssetMeta, variants: Record<string, Blob | StoredBytes>) => {
    const tx = db.transaction(['assets', 'blobs'], 'readwrite');
    try {
      tx.objectStore('assets').put(meta);
      for (const [v, value] of Object.entries(variants)) tx.objectStore('blobs').put(value, `${meta.assetId}/${v}`);
    } catch (err) {
      tx.abort(); // never the metadata without its variants
      throw err;
    }
    await committed(tx);
  };
  return {
    async putAsset(meta, variants) {
      blobsOk ??= canStoreBlobs(`${name}-probe`);
      if (await blobsOk) return write(meta, variants);
      const bytes = await Promise.all(Object.entries(variants).map(async ([v, b]) => [v, { type: b.type, bytes: await b.arrayBuffer() }] as const));
      await write(meta, Object.fromEntries(bytes));
    },
    async loadAll() {
      const tx = db.transaction(['assets', 'blobs'], 'readonly');
      const metas = await done(tx.objectStore('assets').getAll() as IDBRequest<AssetMeta[]>);
      const keys = await done(tx.objectStore('blobs').getAllKeys());
      const blobs = await done(tx.objectStore('blobs').getAll() as IDBRequest<Array<Blob | StoredBytes>>);
      const byAsset = new Map<string, VariantBlobs>();
      keys.forEach((k, i) => {
        const [assetId, v] = String(k).split('/');
        if (!assetId || !v) return;
        const entry = byAsset.get(assetId) ?? {};
        const value = blobs[i]!;
        entry[v as StoredVariant] = value instanceof Blob ? value : new Blob([value.bytes], { type: value.type });
        byAsset.set(assetId, entry);
      });
      return metas.map((meta) => ({ meta: withDefaults(meta), variants: byAsset.get(meta.assetId) ?? {} }));
    },
    async putUpload(r) {
      const tx = db.transaction('uploads', 'readwrite');
      tx.objectStore('uploads').put(r);
      await committed(tx);
    },
    async listUploads(boardId) {
      const all = await done(db.transaction('uploads', 'readonly').objectStore('uploads').getAll() as IDBRequest<UploadRecord[]>);
      return all.filter((r) => r.boardId === boardId);
    },
    async deleteUpload(assetId) {
      const tx = db.transaction('uploads', 'readwrite');
      tx.objectStore('uploads').delete(assetId);
      await committed(tx);
    },
    close: () => db.close(),
  };
}

/**
 * Images imported on this device: blobs in IndexedDB, served to <img> through object URLs created at load and
 * revoked on close. Server boards also keep their pending uploads here.
 */
export class LocalMediaStore implements MediaResolver {
  private readonly byId = new Map<string, AssetMeta>();
  private readonly bySha = new Map<string, AssetMeta>();
  private readonly urls = new Map<string, string>();
  private readonly blobs = new Map<string, Blob>();

  private constructor(
    private readonly backend: MediaBackend,
    private readonly reducedMotion: boolean,
  ) {}

  static async open(backend?: MediaBackend, opts: { reducedMotion?: boolean } = {}): Promise<LocalMediaStore> {
    const store = new LocalMediaStore(backend ?? (await indexedDbBackend()), opts.reducedMotion ?? false);
    for (const { meta, variants } of await store.backend.loadAll()) store.add(meta, variants);
    return store;
  }

  async putAsset(meta: AssetMeta, variants: VariantBlobs): Promise<void> {
    await this.backend.putAsset(meta, variants);
    this.add(meta, variants);
  }

  findBySha(sha256: string): AssetMeta | undefined {
    return this.bySha.get(sha256);
  }

  get(assetId: string): AssetMeta | undefined {
    return this.byId.get(assetId);
  }

  info(assetId: string): { w: number; h: number } | null {
    const m = this.byId.get(assetId);
    return m ? { w: m.w, h: m.h } : null;
  }

  blob(assetId: string, v: StoredVariant): Blob | undefined {
    return this.blobs.get(`${assetId}/${v}`);
  }

  urlFor(assetId: string, variant: Variant): string {
    const meta = this.byId.get(assetId);
    if (!meta) return '';
    return this.urls.get(`${assetId}/${resolveVariant(meta, variant, this.reducedMotion)}`) ?? '';
  }

  /** The server already had this content under `to`: the local blobs now also answer for `to` (this session). */
  alias(from: string, to: string): void {
    const meta = this.byId.get(from);
    if (!meta || from === to) return;
    this.byId.set(to, { ...meta, assetId: to });
    for (const [k, url] of [...this.urls]) if (k.startsWith(`${from}/`)) this.urls.set(`${to}/${k.slice(from.length + 1)}`, url);
    for (const [k, b] of [...this.blobs]) if (k.startsWith(`${from}/`)) this.blobs.set(`${to}/${k.slice(from.length + 1)}`, b);
  }

  putUpload(r: UploadRecord): Promise<void> {
    return this.backend.putUpload(r);
  }

  listUploads(boardId: string): Promise<UploadRecord[]> {
    return this.backend.listUploads(boardId);
  }

  deleteUpload(assetId: string): Promise<void> {
    return this.backend.deleteUpload(assetId);
  }

  close(): void {
    for (const url of new Set(this.urls.values())) URL.revokeObjectURL(url);
    this.urls.clear();
    this.blobs.clear();
    this.backend.close();
  }

  private add(meta: AssetMeta, variants: VariantBlobs): void {
    this.byId.set(meta.assetId, meta);
    if (meta.sha256) this.bySha.set(meta.sha256, meta); // empty when hashing was unavailable
    for (const [v, blob] of Object.entries(variants)) {
      this.blobs.set(`${meta.assetId}/${v}`, blob);
      this.urls.set(`${meta.assetId}/${v}`, URL.createObjectURL(blob));
    }
  }
}
